import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import VmoContainer from "../Elements/VmoContainer";
import VmoButton from "../Elements/VmoButton/VmoButton";
import VmoForm from "../Collections/VmoForm/VmoForm";
import VmoCard from "../Views/VmoCard/VmoCard";
import VmoInput from "../Elements/VmoInput";
import VmoGrid from "../Collections/VmoGrid/VmoGrid";
import VmoGridColumn from "../Collections/VmoGrid/VmoGridColumn";
import VmoGridRow from "../Collections/VmoGrid/VmoGridRow";
import VmoButtonGroup from "../Elements/VmoButton/VmoButtonGroup";
import VmoBrandLogo from "../Custom/VmoBrandLogo";
import VmoCardContent from "../Views/VmoCard/VmoCardContent";
import VmoButtonOr from "../Elements/VmoButton/VmoButtonOr";
import { useCallback } from "react";
import VmoHeader from "../Elements/VmoHeader/VmoHeader";

export default function VmoOTPVerification({
  otpLength = 6,
  inputLabel = null,
  btnLabel = "Verify",
  header = "Check your email",
  subHeader = "We just sent a six-digit confirmation code to:",
  helperMessage = "Your code will expire in 5 minutes. Enter it soon!\nKeep this window open while checking for your code.",
  resendOtpBtnProps = {
    label: "Resend OTP",
    onClick: (requestId) => null,
  },
  changeEmailProps = {
    label: "SignUp with a new email address",
  },
  onVerifyOTP = () => null,
  onlyForm = false,
  testIds = {},
}) {
  const [otpCounter, setOtpCounter] = useState(0);
  const [error, setError] = useState(null);
  const [code, setCode] = useState(Array(otpLength).fill(""));
  const [requestId, setRequestId] = useState(null);
  const inputRefs = Array(otpLength)
    .fill(null)
    .map(() => useRef(null));

  useEffect(() => {
    let interval;
    if (otpCounter > 0) {
      interval = setInterval(() => setOtpCounter((s) => s - 1), 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [otpCounter]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const otpFromUrl = urlParams.get("otp");
    const requestId = urlParams.get("requestId");

    if (otpFromUrl && otpFromUrl.length === 6) {
      setCode(otpFromUrl.split(""));
    }
    if (requestId) {
      setRequestId(requestId);
    }
  }, []);

  const showLeadMessage = helperMessage.replace(/\n/g, "<br />");
  const isAllFieldsFilled =
    code.every((c) => c !== "") && code.length === otpLength;
  const focusOnEmptyInput = useCallback(() => {
    const emptyIndex = code.findIndex((c) => c === "");
    if (emptyIndex !== -1) {
      inputRefs[emptyIndex].current.focus();
    }
  }, [code, inputRefs]);

  const onResendOTP = () => {
    setOtpCounter(30);
    resendOtpBtnProps.onClick(requestId);
  };

  const onChangeEmail = () => {
    changeEmailProps?.onClick();
  };

  const onVerify = () => {
    const otp = code.join("");
    if (!isAllFieldsFilled) {
      const errMsg = `Please enter ${otpLength} digit OTP`;
      setError(errMsg);
      focusOnEmptyInput();
      return;
    }
    const data = {
      otp,
      requestId,
    };
    onVerifyOTP(data);
  };

  const handleInputChange = (index, value, e) => {
    switch (e.key) {
      case "ArrowLeft":
        if (index > 0) {
          inputRefs[index - 1].current.focus();
          e.preventDefault();
        }
        break;
      case "ArrowRight":
        if (index < otpLength - 1) {
          inputRefs[index + 1].current.focus();
          e.preventDefault();
        }
        break;
      case "Backspace":
        if (value === "") {
          if (index > 0) {
            inputRefs[index - 1].current.focus();
          }
          e.preventDefault();
        }
        break;
      case " ":
      case "Spacebar":
      case "Space":
        e.preventDefault();
        break;
      default:
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        if (value !== "" && index < otpLength - 1) {
          inputRefs[index + 1].current.focus();
        }
        break;
    }
  };

  const handleOptPaste = (e, index) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedText.length <= otpLength - index) {
      const newCode = [...code];
      for (let i = index; i < index + pastedText.length; i++) {
        newCode[i] = pastedText[i - index];
      }
      setCode(newCode);

      if (pastedText.length > 0) {
        inputRefs[otpLength - 1].current.focus();
      }
    }
  };

  function _renderOTPInput() {
    return (
      <React.Fragment>
        <VmoGrid data-testid={testIds.otpGrid}>
          <VmoGridRow columns="equal">
            {Array(otpLength)
              .fill("")
              .map((_, index) => (
                <VmoGridColumn key={index}>
                  <VmoInput
                    ref={inputRefs[index]}
                    value={code[index]}
                    maxLength="1"
                    onChange={(e) =>
                      handleInputChange(index, e.target.value, e)
                    }
                    onKeyDown={(e) =>
                      handleInputChange(index, e.target.value, e)
                    }
                    onPaste={(e) => handleOptPaste(e, index)}
                    data-testid={`${testIds.otpInput}-${index}`}
                  />
                </VmoGridColumn>
              ))}
          </VmoGridRow>
          {error && (
            <VmoHeader
              size="small"
              color="red"
              as="small"
              data-testid={testIds.errorMsg}
              style={{ margin: "0px" }}
            >
              {error}
            </VmoHeader>
          )}
        </VmoGrid>
        <VmoButton
          primary
          type="submit"
          fluid
          size="large"
          data-testid={testIds.verifyButton}
        >
          {btnLabel}
        </VmoButton>
      </React.Fragment>
    );
  }

  function _renderOTPForm() {
    return (
      <React.Fragment>
        {inputLabel && <h3>{inputLabel}</h3>}
        <VmoForm onSubmit={onVerify} data-testid={testIds.otpForm}>
          {_renderOTPInput()}
        </VmoForm>
      </React.Fragment>
    );
  }

  function _renderLayout() {
    return (
      <VmoContainer data-testid={testIds.container}>
        <div className="signInPageHeader">
          <VmoBrandLogo className={"veemo-logo"} data-testid={testIds.logo} />
        </div>
        <div className="signInPage">
          <div className="signInBody">
            <h2>{header}</h2>
            <p className="vmo-lead">{subHeader}</p>
            <VmoCard className="loginCard otpCard" data-testid={testIds.card}>
              <VmoCardContent>{_renderOTPForm()}</VmoCardContent>
            </VmoCard>

            <p
              dangerouslySetInnerHTML={{ __html: showLeadMessage }}
              className="vmo-lead"
            />
            {otpCounter > 0 && `Resend OTP in ${otpCounter} seconds`}
            {!Boolean(otpCounter) && (
              <VmoButtonGroup>
                <React.Fragment>
                  <VmoButton
                    disabled={Boolean(otpCounter)}
                    onClick={onResendOTP}
                    data-testid={testIds.resendButton}
                  >
                    {resendOtpBtnProps?.label}
                  </VmoButton>
                  <VmoButtonOr />
                </React.Fragment>
                <VmoButton
                  onClick={onChangeEmail}
                  data-testid={testIds.changeEmailButton}
                >
                  {changeEmailProps?.label}
                </VmoButton>
              </VmoButtonGroup>
            )}
          </div>
        </div>
      </VmoContainer>
    );
  }

  if (onlyForm) {
    return _renderOTPForm();
  }
  return _renderLayout();
}

VmoOTPVerification.propTypes = {
  otpLength: PropTypes.number,
  inputLabel: PropTypes.string,
  btnLabel: PropTypes.string,
  header: PropTypes.string,
  subHeader: PropTypes.string,
  helperMessage: PropTypes.string,
  onlyForm: PropTypes.bool,
  resendOtpBtnProps: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  changeEmailProps: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  }),
  onVerifyOTP: PropTypes.func.isRequired,
  testIds: PropTypes.shape({
    container: PropTypes.string,
    logo: PropTypes.string,
    card: PropTypes.string,
    otpForm: PropTypes.string,
    otpGrid: PropTypes.string,
    otpInput: PropTypes.string,
    errorMsg: PropTypes.string,
    verifyButton: PropTypes.string,
    resendButton: PropTypes.string,
    changeEmailButton: PropTypes.string,
  }),
};

VmoOTPVerification.defaultProps = {
  otpLength: 6,
  inputLabel: null,
  btnLabel: "Verify",
  header: "Check your email",
  subHeader: "We just sent a six-digit confirmation code to:",
  helperMessage:
    "Your code will expire in 5 minutes. Enter it soon!\nKeep this window open while checking for your code.",
  resendOtpBtnProps: {
    label: "Resend OTP",
    onClick: (requestId) => null,
  },
  changeEmailProps: {
    label: "SignUp with a new email address",
  },
  onVerifyOTP: () => null,
  onlyForm: false,
  testIds: {
    container: "container",
    logo: "logo",
    card: "card",
    otpForm: "otp-form",
    otpGrid: "otp-grid",
    otpInput: "otp-input",
    errorMsg: "error-msg",
    verifyButton: "verify-button",
    resendButton: "resend-button",
    changeEmailButton: "change-email-button",
  },
};
